import React, { useState } from "react";
import { 
  FaUserFriends, FaChild, FaAccessibleIcon, 
  FaPeopleArrows, FaHandsHelping, FaChevronDown, FaStethoscope, FaShieldAlt, FaArrowRight, FaEnvelope 
} from "react-icons/fa";
import presentationVideo from "../../Asset/hetep_video_presentation.mp4";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useColorMode } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";

// --- COMPOSANT ACCORDÉON PREMIUM ---
const Accordion = ({ title, content, isDark }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`mb-4 border-b ${isDark ? "border-gray-800" : "border-gray-200"}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center py-6 md:py-8 text-left group transition-all focus:outline-none"
      >
        <span className={`text-xl md:text-2xl font-bold transition-colors duration-300 ${
          isOpen ? "text-blue-600" : isDark ? "text-white group-hover:text-blue-400" : "text-[#1B365D] group-hover:text-blue-600"
        }`}>
          {title}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition-colors ${
              isOpen ? "bg-blue-600 text-white" : isDark ? "bg-gray-800 text-gray-400 group-hover:bg-gray-700" : "bg-blue-50 text-blue-600 group-hover:bg-blue-100"
          }`}
        >
          <FaChevronDown size={14} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <p className={`pb-8 text-base md:text-lg leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {content}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Presentation = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const [activeTab, setActiveTab] = useState(0); // État pour gérer l'onglet actif du menu interactif

  const sectionVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.1 } },
  };

  return (
    <>
      <Helmet>
        <title>Notre Expertise — Hetep Iaout Services depuis 2005</title>
        <meta name="description" content="Découvrez Hetep Iaout Services, acteur de l'aide à domicile depuis plus de 19 ans en Île-de-France. Notre histoire, nos valeurs et notre expertise au service des familles." />
        <link rel="canonical" href="https://www.hisvie.com/Presentation/" />
      </Helmet>

      <div className={`min-h-screen overflow-hidden transition-colors duration-300 ${isDark ? 'bg-[#0a0f16]' : 'bg-[#FAFCFF]'}`}>
        
       {/* --- HERO SECTION --- */}
        <div className="relative pt-28 pb-16 md:pt-32 md:pb-24 px-4 md:px-6 max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-12">
            {/* Taches de couleur (Glows) en fond pour le côté premium */}
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-[100px] -z-10 pointer-events-none"></div>
            <div className="absolute top-40 right-10 w-72 h-72 bg-yellow-400/10 rounded-full blur-[100px] -z-10 pointer-events-none"></div>

            {/* --- Colonne Gauche : Texte --- */}
            <motion.div 
                className="w-full lg:w-3/5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-100'}`}>
                   
                    <span className={`text-xs font-bold tracking-widest uppercase ${isDark ? 'text-blue-400' : 'text-blue-800'}`}>
                        Hetep-Iaout Service
                    </span>
                </div>

                <h1 className={`text-4xl sm:text-5xl md:text-7xl font-black leading-[1.05] tracking-tight mb-8 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                    Notre Expertise. <br className="hidden md:block"/>
                    <span className="text-blue-600 font-serif italic font-light">Nos Valeurs.</span>
                </h1>

                {/* Barre latérale décorative pour structurer le texte */}
                <div className="flex flex-col gap-6 border-l-4 border-blue-600 pl-6 md:pl-8 py-2">
                    <p className={`text-lg md:text-2xl leading-relaxed font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Bienvenue chez <span className="font-bold text-blue-600">HIS</span>, où notre mission est de fournir des services spécialisés aux personnes âgées, en situation de handicap ou atteintes de maladies rares.
                    </p>
                    <p className={`text-base md:text-lg leading-relaxed max-w-2xl ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Nos intervenants qualifiés disposent d'une expertise unique dans la prise en charge de cas complexes (tels que la trachéotomie ou les maladies neurodégénératives), assurant sécurité, dignité et qualité à domicile.
                    </p>
                </div>
            </motion.div>

           {/* Conteneur de la vidéo (Style iPhone / Premium) */}
                <div className={`relative rounded-[2.5rem] overflow-hidden shadow-2xl border-[6px] max-w-[300px] w-full aspect-[9/16] bg-black ${isDark ? 'border-gray-800 shadow-blue-900/20' : 'border-white shadow-blue-900/10'}`}>
                    <video 
                        src={presentationVideo}
                        controls 
                        playsInline 
                        className="w-full h-full object-cover"
                    />
                </div>
            
        </div>

        {/* --- MENU INTERACTIF : DOMAINES D'EXPERTISE (STYLE APP) --- */}
        <motion.section 
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="max-w-7xl mx-auto px-4 md:px-6 pb-24 md:pb-32 relative z-20"
        >
            <div className="mb-10 md:mb-16">
                <h2 className={`text-3xl md:text-5xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                    Nos <span className="text-blue-600">Compétences.</span>
                </h2>
                <p className={`text-sm md:text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Sélectionnez un domaine pour découvrir notre approche.
                </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 md:gap-10">
                
                {/* 1. LE MENU LATÉRAL (Scrollable sur mobile, Liste sur PC) */}
                <div className="w-full lg:w-1/3 flex lg:flex-col gap-2 overflow-x-auto pb-4 lg:pb-0 css-hide-scrollbar snap-x">
                    {[
                        { icon: <FaStethoscope size={20} />, short: "Soins médicaux" },
                        { icon: <FaAccessibleIcon size={20} />, short: "Trachéotomie" },
                        { icon: <FaHandsHelping size={20} />, short: "Aide quotidienne" },
                        { icon: <FaPeopleArrows size={20} />, short: "Mobilité" },
                        { icon: <FaUserFriends size={20} />, short: "Maladies Rares" },
                        { icon: <FaChild size={20} />, short: "Pédiatrie" },
                    ].map((tab, index) => {
                        const isActive = activeTab === index;
                        return (
                            <button
                                key={index}
                                onClick={() => setActiveTab(index)}
                                className={`snap-start shrink-0 flex items-center gap-4 px-6 py-4 rounded-2xl font-bold transition-all duration-300 text-left ${
                                    isActive 
                                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30" 
                                    : isDark 
                                        ? "bg-transparent text-gray-400 hover:bg-gray-800 hover:text-white" 
                                        : "bg-transparent text-gray-500 hover:bg-blue-50 hover:text-[#1B365D]"
                                }`}
                            >
                                <div className={`${isActive ? "text-white" : isDark ? "text-gray-500" : "text-gray-400"} transition-colors`}>
                                    {tab.icon}
                                </div>
                                <span className="whitespace-nowrap md:whitespace-normal">{tab.short}</span>
                            </button>
                        );
                    })}
                </div>

                {/* 2. LA ZONE DE CONTENU DYNAMIQUE */}
                <div className="w-full lg:w-2/3">
                    <div className={`relative overflow-hidden h-full min-h-[350px] p-8 md:p-12 rounded-[2rem] md:rounded-[3rem] border shadow-xl ${isDark ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-blue-50'}`}>
                        
                        {/* Glow décoratif */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] pointer-events-none"></div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
                                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                exit={{ opacity: 0, y: -20, filter: "blur(4px)" }}
                                transition={{ duration: 0.4, ease: "easeOut" }}
                                className="h-full flex flex-col justify-center"
                            >
                                {activeTab === 0 && (
                                    <>
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-sm ${isDark ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}><FaStethoscope size={28} /></div>
                                        <h3 className={`text-2xl md:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Soins médicaux complexes</h3>
                                        <p className={`text-base md:text-lg leading-relaxed mb-8 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Nos équipes bénéficient d'une formation continue rigoureuse pour la prise en charge de pathologies lourdes directement au domicile, en étroite collaboration avec vos professionnels de santé.
                                        </p>
                                    </>
                                )}
                                {activeTab === 1 && (
                                    <>
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-sm ${isDark ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}><FaAccessibleIcon size={28} /></div>
                                        <h3 className={`text-2xl md:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Expertise Trachéotomie</h3>
                                        <p className={`text-base md:text-lg leading-relaxed mb-8 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Maîtrise absolue des soins techniques avancés, de l'assistance respiratoire (aspiration endo-trachéale) et de la manipulation d'appareillages vitaux pour garantir une sécurité 24h/24.
                                        </p>
                                    </>
                                )}
                                {activeTab === 2 && (
                                    <>
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-sm ${isDark ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}><FaHandsHelping size={28} /></div>
                                        <h3 className={`text-2xl md:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Aide à la vie quotidienne</h3>
                                        <p className={`text-base md:text-lg leading-relaxed mb-8 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Un maintien d’un confort de vie optimal dans le plus grand respect de l'intimité et de la dignité : accompagnement pour la toilette, la préparation des repas et l'aide à la prise.
                                        </p>
                                    </>
                                )}
                                {activeTab === 3 && (
                                    <>
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-sm ${isDark ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}><FaPeopleArrows size={28} /></div>
                                        <h3 className={`text-2xl md:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Mobilité & Transferts</h3>
                                        <p className={`text-base md:text-lg leading-relaxed mb-8 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Assistance hautement sécurisée pour les déplacements intérieurs, les transferts lit-fauteuil avec ou sans lève-personne, et l'accompagnement sécurisé aux activités sociales.
                                        </p>
                                    </>
                                )}
                                {activeTab === 4 && (
                                    <>
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-sm ${isDark ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}><FaUserFriends size={28} /></div>
                                        <h3 className={`text-2xl md:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Maladies Rares & Orphelines</h3>
                                        <p className={`text-base md:text-lg leading-relaxed mb-8 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Une spécialisation pointue et des protocoles sur-mesure dédiés à l'accompagnement des pathologies orphelines, nécessitant une adaptabilité constante de nos équipes.
                                        </p>
                                    </>
                                )}
                                {activeTab === 5 && (
                                    <>
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-sm ${isDark ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}><FaChild size={28} /></div>
                                        <h3 className={`text-2xl md:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Pédiatrie spécialisée</h3>
                                        <p className={`text-base md:text-lg leading-relaxed mb-8 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Un encadrement bienveillant, stimulant et ultra-sécurisant pour les enfants en situation de handicap lourd ou de polyhandicap, afin d'accompagner l'enfant et soulager les parents.
                                        </p>
                                    </>
                                )}

                                {/* Bouton d'action intégré */}
                                <div>
                                    <Link to="/contact" className={`inline-flex items-center gap-2 font-bold text-sm md:text-base transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-[#1B365D]'}`}>
                                        En parler avec un conseiller <FaArrowRight size={14} />
                                    </Link>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </motion.section>

        {/* --- SECTION ACCORDÉON (APPROCHE ÉDITORIALE) --- */}
        <motion.section 
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="max-w-4xl mx-auto px-4 md:px-6 pb-24 md:pb-32"
        >
            <div className="text-center mb-12 md:mb-16">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 shadow-sm text-xs font-bold uppercase tracking-widest ${isDark ? "bg-gray-800/50 border-gray-700 text-gray-300" : "bg-blue-50/50 border-blue-100 text-blue-800"}`}>
                    Sur-mesure
                </div>
                <h2 className={`text-3xl md:text-5xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                    Un accompagnement <br className="hidden md:block"/><span className="text-blue-600 italic font-serif font-light">selon vos besoins</span>
                </h2>
            </div>
          
            <div className={`border-t-2 ${isDark ? "border-gray-800" : "border-gray-200"}`}>
                {[
                    { title: "Personnes en situation de handicap", content: "Nos services répondent à des besoins spécifiques avec un encadrement rigoureusement formé aux soins techniques et à la manipulation d'appareillages médicaux complexes." },
                    { title: "Perte d’autonomie (Seniors)", content: "Nous proposons un accompagnement quotidien global (lever, coucher, toilette, repas) pour assurer sécurité, stimulation et bien-être au domicile." },
                    { title: "Troubles du développement", content: "Un encadrement éducatif et stimulant dédié aux enfants et adultes atteints d'autisme ou de troubles cognitifs du comportement." },
                    { title: "Traumatismes physiques", content: "Un soutien logistique et une rééducation de confort adaptés à la suite d'un accident ou d'une blessure majeure, en lien avec vos professionnels de santé." },
                    { title: "Maladies rares & orphelines", content: "Parce que chaque cas est unique, nous élaborons un suivi extrêmement précis et des soins ajustés aux symptômes de chaque pathologie spécifique." },
                    { title: "Maladies neurodégénératives", content: "Sclérose en plaques, maladie de Parkinson, Alzheimer : nous apportons une aide quotidienne sécurisante associée à un soutien moral indispensable." },
                ].map((item, i) => (
                    <Accordion key={i} title={item.title} content={item.content} isDark={isDark} />
                ))}
            </div>
        </motion.section>

        {/* --- CTA FINAL : CONVERSATION --- */}
        <motion.section
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sectionVariants}
          className="max-w-5xl mx-auto px-4 pb-24"
        >
          <div className={`relative overflow-hidden rounded-[2.5rem] p-10 md:p-16 flex flex-col md:flex-row items-center gap-10 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white shadow-2xl shadow-blue-900/5 border-blue-50'}`}>
            <div className="flex-1 z-10 text-center md:text-left">
              <h2 className={`text-3xl md:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                Prêt à construire un accompagnement <span className="text-blue-600">sur-mesure</span> ?
              </h2>
              <p className={`text-base md:text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Nos coordinateurs et experts HIS sont à votre écoute pour organiser une évaluation gratuite.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto z-10">
              <Link to="/contact" className="w-full sm:w-auto">
                  <button className="flex items-center justify-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-2xl font-bold hover:bg-blue-700 transition-all w-full shadow-lg shadow-blue-600/20">
                      Nous contacter
                  </button>
              </Link>
              <a href="mailto:his@donnerlavieauxannees.com" className="flex items-center justify-center gap-3 bg-yellow-50 text-yellow-700 px-8 py-4 rounded-2xl font-bold hover:bg-yellow-100 transition-all border border-yellow-100">
                  <FaEnvelope /> Email
              </a>
            </div>
          </div>
        </motion.section>

      </div>
    </>
  );
};

export default Presentation;